import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../../../config';
import settingsStore from '../../../store/settingsStore';
import { DEFAULT_PHONE_STATUS } from '@claimup/shared/constants/phones';
import { getPhoneStatusLabel, getPhoneStatusColors } from './phoneUtils';

const AircallPhones = () => {
  const { aircallPhones, refreshAircallPhones, syncAircallPhones } = settingsStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${baseAPI}/users/list?role=company`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(res => setCompanies(res.data || []))
      .catch(() => setCompanies([]));
  }, []);

  const handleCompanyChange = async (phoneId, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${baseAPI}/settings/aircall/${phoneId}`,
        { company_id: value },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success('Empresa actualizada');
      await refreshAircallPhones();
    } catch (err) {
      toast.error('Error al asignar la empresa');
    }
  };

  const loadPhones = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await refreshAircallPhones();
    } catch (err) {
      setError('Error al cargar los teléfonos de Aircall');
      // eslint-disable-next-line no-console
      console.error('Error loading Aircall phones:', err);
    } finally {
      setIsLoading(false);
    }
  }, [refreshAircallPhones]);

  useEffect(() => {
    loadPhones();
  }, [loadPhones]);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    setError(null);
    try {
      const stats = await syncAircallPhones();
      toast.success(
        `Sincronización completada: ${stats.created || 0} creados, ${stats.updated || 0} actualizados, ${stats.deleted || 0} eliminados`
      );
      await loadPhones();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Error al sincronizar los teléfonos de Aircall';
      setError(errorMessage);
      toast.error(errorMessage);
      // eslint-disable-next-line no-console
      console.error('Error syncing Aircall phones:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [syncAircallPhones, loadPhones]);

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-medium text-slate-900'>Teléfonos Aircall</h3>
          <button
            type='button'
            onClick={handleSync}
            disabled={isSyncing}
            className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      </div>
      <div className='p-6'>
        {isLoading ? (
          <div className='text-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto'></div>
            <p className='text-sm text-slate-500 mt-2'>Cargando teléfonos...</p>
          </div>
        ) : error ? (
          <div className='text-center py-8'>
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        ) : aircallPhones.length === 0 ? (
          <div className='text-center py-8'>
            <p className='text-sm text-slate-500'>No hay teléfonos activos.</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-slate-200'>
              <thead className='bg-slate-50'>
                <tr>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Nombre
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    ID
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Número de teléfono
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Estado
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Empresa
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-slate-200'>
                {aircallPhones.map(phone => (
                  <tr key={phone._id}>
                    <td className='px-4 py-3 text-sm text-slate-900'>{phone.nombre || '-'}</td>
                    <td className='px-4 py-3 text-sm text-slate-900'>{phone.phone_id}</td>
                    <td className='px-4 py-3 text-sm text-slate-700'>{phone.phone_number}</td>
                    <td className='px-4 py-3 text-sm'>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPhoneStatusColors(
                          phone.status || DEFAULT_PHONE_STATUS
                        )}`}
                      >
                        {getPhoneStatusLabel(phone.status || DEFAULT_PHONE_STATUS)}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      <select
                        value={phone.company_id || ''}
                        onChange={e => handleCompanyChange(phone._id, e.target.value)}
                        className='px-2 py-1 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 text-sm bg-white'
                      >
                        <option value=''>Compartido (ClaimUp)</option>
                        {companies.map(c => (
                          <option key={c._id} value={c._id}>
                            {`${c.username || ''} ${c.userlastname || ''}`.trim() || c.usermail}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AircallPhones;

